import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../lib/api";
import { PasswordInput } from "../components/auth/PasswordInput";

interface ApiKeyInfo {
  provider: string;
  key_hint: string;
  preferred_model: string | null;
  is_valid: boolean;
  last_validated_at: string | null;
}

interface ProviderModel {
  id: string;
  name: string;
}

interface ProviderConfig {
  name: string;
  placeholder: string;
  icon: string;
}

const PROVIDERS: Record<string, ProviderConfig> = {
  openai: {
    name: "OpenAI",
    placeholder: "sk-...",
    icon: "🤖",
  },
  anthropic: {
    name: "Anthropic",
    placeholder: "sk-ant-...",
    icon: "🔮",
  },
  google: {
    name: "Google",
    placeholder: "AIza...",
    icon: "🔷",
  },
  xai: {
    name: "xAI (Grok)",
    placeholder: "xai-...",
    icon: "🅧",
  },
  perplexity: {
    name: "Perplexity",
    placeholder: "pplx-...",
    icon: "🔍",
  },
};

// All AssemblyAI supported languages (99+)
const LANGUAGE_OPTIONS = [
  { value: "auto", label: "🌐 Auto-detect" },
  { value: "en", label: "🇬🇧 English" },
  { value: "en_au", label: "🇦🇺 English (Australia)" },
  { value: "en_uk", label: "🇬🇧 English (UK)" },
  { value: "en_us", label: "🇺🇸 English (US)" },
  { value: "es", label: "🇪🇸 Spanish" },
  { value: "fr", label: "🇫🇷 French" },
  { value: "de", label: "🇩🇪 German" },
  { value: "it", label: "🇮🇹 Italian" },
  { value: "pt", label: "🇵🇹 Portuguese" },
  { value: "nl", label: "🇳🇱 Dutch" },
  { value: "af", label: "🇿🇦 Afrikaans" },
  { value: "sq", label: "🇦🇱 Albanian" },
  { value: "am", label: "🇪🇹 Amharic" },
  { value: "ar", label: "🇸🇦 Arabic" },
  { value: "hy", label: "🇦🇲 Armenian" },
  { value: "as", label: "🇮🇳 Assamese" },
  { value: "az", label: "🇦🇿 Azerbaijani" },
  { value: "ba", label: "🇷🇺 Bashkir" },
  { value: "eu", label: "🇪🇸 Basque" },
  { value: "be", label: "🇧🇾 Belarusian" },
  { value: "bn", label: "🇧🇩 Bengali" },
  { value: "bs", label: "🇧🇦 Bosnian" },
  { value: "br", label: "🇫🇷 Breton" },
  { value: "bg", label: "🇧🇬 Bulgarian" },
  { value: "my", label: "🇲🇲 Burmese" },
  { value: "ca", label: "🇪🇸 Catalan" },
  { value: "zh", label: "🇨🇳 Chinese" },
  { value: "hr", label: "🇭🇷 Croatian" },
  { value: "cs", label: "🇨🇿 Czech" },
  { value: "da", label: "🇩🇰 Danish" },
  { value: "et", label: "🇪🇪 Estonian" },
  { value: "fo", label: "🇫🇴 Faroese" },
  { value: "fi", label: "🇫🇮 Finnish" },
  { value: "gl", label: "🇪🇸 Galician" },
  { value: "ka", label: "🇬🇪 Georgian" },
  { value: "el", label: "🇬🇷 Greek" },
  { value: "gu", label: "🇮🇳 Gujarati" },
  { value: "ht", label: "🇭🇹 Haitian Creole" },
  { value: "ha", label: "🇳🇬 Hausa" },
  { value: "haw", label: "🇺🇸 Hawaiian" },
  { value: "he", label: "🇮🇱 Hebrew" },
  { value: "hi", label: "🇮🇳 Hindi" },
  { value: "hu", label: "🇭🇺 Hungarian" },
  { value: "is", label: "🇮🇸 Icelandic" },
  { value: "id", label: "🇮🇩 Indonesian" },
  { value: "ga", label: "🇮🇪 Irish" },
  { value: "ja", label: "🇯🇵 Japanese" },
  { value: "jw", label: "🇮🇩 Javanese" },
  { value: "kn", label: "🇮🇳 Kannada" },
  { value: "kk", label: "🇰🇿 Kazakh" },
  { value: "km", label: "🇰🇭 Khmer" },
  { value: "ko", label: "🇰🇷 Korean" },
  { value: "lo", label: "🇱🇦 Lao" },
  { value: "la", label: "🇻🇦 Latin" },
  { value: "lv", label: "🇱🇻 Latvian" },
  { value: "ln", label: "🇨🇩 Lingala" },
  { value: "lt", label: "🇱🇹 Lithuanian" },
  { value: "lb", label: "🇱🇺 Luxembourgish" },
  { value: "mk", label: "🇲🇰 Macedonian" },
  { value: "mg", label: "🇲🇬 Malagasy" },
  { value: "ms", label: "🇲🇾 Malay" },
  { value: "ml", label: "🇮🇳 Malayalam" },
  { value: "mt", label: "🇲🇹 Maltese" },
  { value: "mi", label: "🇳🇿 Maori" },
  { value: "mr", label: "🇮🇳 Marathi" },
  { value: "mn", label: "🇲🇳 Mongolian" },
  { value: "ne", label: "🇳🇵 Nepali" },
  { value: "no", label: "🇳🇴 Norwegian" },
  { value: "nn", label: "🇳🇴 Norwegian Nynorsk" },
  { value: "oc", label: "🇫🇷 Occitan" },
  { value: "ps", label: "🇦🇫 Pashto" },
  { value: "fa", label: "🇮🇷 Persian" },
  { value: "pl", label: "🇵🇱 Polish" },
  { value: "pa", label: "🇮🇳 Punjabi" },
  { value: "ro", label: "🇷🇴 Romanian" },
  { value: "ru", label: "🇷🇺 Russian" },
  { value: "sa", label: "🇮🇳 Sanskrit" },
  { value: "sr", label: "🇷🇸 Serbian" },
  { value: "sn", label: "🇿🇼 Shona" },
  { value: "sd", label: "🇵🇰 Sindhi" },
  { value: "si", label: "🇱🇰 Sinhala" },
  { value: "sk", label: "🇸🇰 Slovak" },
  { value: "sl", label: "🇸🇮 Slovenian" },
  { value: "so", label: "🇸🇴 Somali" },
  { value: "su", label: "🇮🇩 Sundanese" },
  { value: "sw", label: "🇰🇪 Swahili" },
  { value: "sv", label: "🇸🇪 Swedish" },
  { value: "tl", label: "🇵🇭 Tagalog" },
  { value: "tg", label: "🇹🇯 Tajik" },
  { value: "ta", label: "🇮🇳 Tamil" },
  { value: "tt", label: "🇷🇺 Tatar" },
  { value: "te", label: "🇮🇳 Telugu" },
  { value: "th", label: "🇹🇭 Thai" },
  { value: "bo", label: "🇨🇳 Tibetan" },
  { value: "tr", label: "🇹🇷 Turkish" },
  { value: "tk", label: "🇹🇲 Turkmen" },
  { value: "uk", label: "🇺🇦 Ukrainian" },
  { value: "ur", label: "🇵🇰 Urdu" },
  { value: "uz", label: "🇺🇿 Uzbek" },
  { value: "vi", label: "🇻🇳 Vietnamese" },
  { value: "cy", label: "🏴󠁧󠁢󠁷󠁬󠁳󠁿 Welsh" },
  { value: "yi", label: "🇮🇱 Yiddish" },
  { value: "yo", label: "🇳🇬 Yoruba" },
];

export default function SettingsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // API Keys state
  const [savedKeys, setSavedKeys] = useState<Record<string, ApiKeyInfo>>({});
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});
  const [selectedModels, setSelectedModels] = useState<Record<string, string>>({});
  const [availableModels, setAvailableModels] = useState<Record<string, ProviderModel[]>>({});
  const [defaultModels, setDefaultModels] = useState<Record<string, string>>({});
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [savingProvider, setSavingProvider] = useState<string | null>(null);
  const [deletingProvider, setDeletingProvider] = useState<string | null>(null);

  // Preferences state
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("tempo_language") || "auto";
  });
  const [languageSearch, setLanguageSearch] = useState("");
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("tempo_dark_mode") === "true";
  });
  
  // Privacy state
  const [trainingOptOut, setTrainingOptOut] = useState(false);
  const [uploadConsent, setUploadConsent] = useState(false);
  const [loadingPrivacy, setLoadingPrivacy] = useState(true);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [savingConsent, setSavingConsent] = useState(false);

  // Subscriptions state
  const [autoTranscribeSubs, setAutoTranscribeSubs] = useState(false);
  const [savingAutoTranscribeSubs, setSavingAutoTranscribeSubs] = useState(false);
  
  // Speaker preferences state (Persona mode)
  interface Speaker {
    id: string;
    canonical_name: string;
    total_speaking_ms: number;
    episode_count: number;
    enabled: boolean;
  }
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loadingSpeakers, setLoadingSpeakers] = useState(true);
  const [togglingSpeaker, setTogglingSpeaker] = useState<string | null>(null);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [authLoading, user, navigate]);

  // Fetch privacy settings
  useEffect(() => {
    const fetchPrivacy = async () => {
      try {
        const [trainingData, consentData, autoTranscribeData] = await Promise.all([
          apiFetch<{ training_opt_out: boolean }>("/settings/training-opt-out"),
          apiFetch<{ upload_consent: boolean }>("/settings/consent"),
          apiFetch<{ enabled: boolean }>("/settings/auto-transcribe-subscriptions"),
        ]);
        setTrainingOptOut(trainingData.training_opt_out);
        setUploadConsent(consentData.upload_consent);
        setAutoTranscribeSubs(autoTranscribeData.enabled);
      } catch (err) {
        console.error("Failed to fetch privacy settings:", err);
      } finally {
        setLoadingPrivacy(false);
      }
    };
    if (user) fetchPrivacy();
  }, [user]);
  
  // Fetch speaker preferences (Persona mode)
  useEffect(() => {
    const fetchSpeakers = async () => {
      try {
        const data = await apiFetch<{ speakers: Speaker[] }>("/settings/speakers");
        setSpeakers(data.speakers || []);
      } catch (err) {
        console.error("Failed to fetch speakers:", err);
      } finally {
        setLoadingSpeakers(false);
      }
    };
    if (user) fetchSpeakers();
  }, [user]);

  // Fetch saved keys on mount
  useEffect(() => {
    const fetchKeys = async () => {
      try {
        const data = await apiFetch<{ keys: ApiKeyInfo[] }>("/settings/api-keys");
        const keysMap: Record<string, ApiKeyInfo> = {};
        for (const key of data.keys) {
          keysMap[key.provider] = key;
        }
        setSavedKeys(keysMap);
        
        // Pre-fill selected models from saved preferences
        const models: Record<string, string> = {};
        for (const key of data.keys) {
          if (key.preferred_model) {
            models[key.provider] = key.preferred_model;
          }
        }
        setSelectedModels(models);
      } catch (err) {
        console.error("Failed to fetch API keys:", err);
      } finally {
        setLoadingKeys(false);
      }
    };
    if (user) fetchKeys();
  }, [user]);

  // Fetch available models for each provider
  useEffect(() => {
    const fetchModels = async () => {
      for (const provider of Object.keys(PROVIDERS)) {
        try {
          const data = await apiFetch<{
            provider: string;
            models: ProviderModel[];
            default_model: string;
          }>(`/settings/api-keys/${provider}/models`);
          setAvailableModels((prev) => ({ ...prev, [provider]: data.models }));
          setDefaultModels((prev) => ({ ...prev, [provider]: data.default_model }));
        } catch (err) {
          console.error(`Failed to fetch models for ${provider}:`, err);
        }
      }
    };
    fetchModels();
  }, []);

  const handleSaveKey = async (provider: string) => {
    const apiKey = keyInputs[provider]?.trim();
    if (!apiKey) {
      setToast({ message: "Please enter an API key", type: "error" });
      return;
    }

    setSavingProvider(provider);
    try {
      const data = await apiFetch<{
        provider: string;
        key_hint: string;
        preferred_model: string | null;
        is_valid: boolean;
        message: string;
      }>(`/settings/api-keys/${provider}`, {
        method: "PUT",
        body: JSON.stringify({
          api_key: apiKey,
          preferred_model: selectedModels[provider] || null,
        }),
      });

      setSavedKeys((prev) => ({
        ...prev,
        [provider]: {
          provider: data.provider,
          key_hint: data.key_hint,
          preferred_model: data.preferred_model,
          is_valid: data.is_valid,
          last_validated_at: new Date().toISOString(),
        },
      }));
      setKeyInputs((prev) => ({ ...prev, [provider]: "" }));
      setToast({ message: data.message, type: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save key";
      setToast({ message, type: "error" });
    } finally {
      setSavingProvider(null);
    }
  };

  const handleDeleteKey = async (provider: string) => {
    setDeletingProvider(provider);
    try {
      const data = await apiFetch<{ provider: string; message: string }>(
        `/settings/api-keys/${provider}`,
        { method: "DELETE" }
      );
      setSavedKeys((prev) => {
        const next = { ...prev };
        delete next[provider];
        return next;
      });
      setSelectedModels((prev) => {
        const next = { ...prev };
        delete next[provider];
        return next;
      });
      setToast({ message: data.message, type: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete key";
      setToast({ message, type: "error" });
    } finally {
      setDeletingProvider(null);
    }
  };

  const handleLanguageChange = (value: string) => {
    setLanguage(value);
    localStorage.setItem("tempo_language", value);
    setToast({ message: "Language preference saved", type: "success" });
  };

  const handleDarkModeChange = (enabled: boolean) => {
    setDarkMode(enabled);
    localStorage.setItem("tempo_dark_mode", String(enabled));
    if (enabled) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    setToast({ message: enabled ? "Dark mode enabled" : "Light mode enabled", type: "success" });
  };

  // Apply dark mode on mount
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    }
  }, []);

  const handleTrainingOptOutChange = async (optOut: boolean) => {
    setSavingPrivacy(true);
    try {
      const data = await apiFetch<{ training_opt_out: boolean; message: string }>(
        "/settings/training-opt-out",
        {
          method: "POST",
          body: JSON.stringify({ opt_out: optOut }),
        }
      );
      setTrainingOptOut(data.training_opt_out);
      setToast({ message: data.message, type: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save setting";
      setToast({ message, type: "error" });
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleUploadConsentChange = async (consent: boolean) => {
    setSavingConsent(true);
    try {
      const data = await apiFetch<{ upload_consent: boolean; message: string }>(
        "/settings/consent",
        {
          method: "POST",
          body: JSON.stringify({ upload_consent: consent }),
        }
      );
      setUploadConsent(data.upload_consent);
      setToast({ message: data.message, type: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save setting";
      setToast({ message, type: "error" });
    } finally {
      setSavingConsent(false);
    }
  };
  
  const handleAutoTranscribeSubsChange = async (enabled: boolean) => {
    setSavingAutoTranscribeSubs(true);
    try {
      const data = await apiFetch<{ enabled: boolean; message: string }>(
        "/settings/auto-transcribe-subscriptions",
        {
          method: "POST",
          body: JSON.stringify({ enabled }),
        }
      );
      setAutoTranscribeSubs(data.enabled);
      setToast({ message: data.message, type: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save setting";
      setToast({ message, type: "error" });
    } finally {
      setSavingAutoTranscribeSubs(false);
    }
  };

  const handleSpeakerToggle = async (speakerId: string, enabled: boolean) => {
    setTogglingSpeaker(speakerId);
    try {
      await apiFetch<{ ok: boolean; message: string }>(
        "/settings/speakers",
        {
          method: "POST",
          body: JSON.stringify({ speaker_id: speakerId, enabled }),
        }
      );
      setSpeakers(prev => prev.map(s => 
        s.id === speakerId ? { ...s, enabled } : s
      ));
      const speaker = speakers.find(s => s.id === speakerId);
      const action = enabled ? "enabled" : "disabled";
      setToast({ message: `${speaker?.canonical_name} ${action} for Persona mode`, type: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save setting";
      setToast({ message, type: "error" });
    } finally {
      setTogglingSpeaker(null);
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect
  }

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex h-[calc(100dvh-68px)] overflow-hidden">
      {/* Left sidebar */}
      <aside className="hidden md:block w-48 shrink-0 border-r border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Settings</h2>
        <nav className="space-y-1">
          <button
            onClick={() => scrollToSection("api-keys")}
            className="w-full text-left px-3 py-2 text-sm rounded-md text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            🔑 API Keys
          </button>
          <button
            onClick={() => scrollToSection("preferences")}
            className="w-full text-left px-3 py-2 text-sm rounded-md text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            ⚙️ Preferences
          </button>
          <button
            onClick={() => scrollToSection("subscriptions")}
            className="w-full text-left px-3 py-2 text-sm rounded-md text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            📡 Subscriptions
          </button>
          <button
            onClick={() => scrollToSection("speakers")}
            className="w-full text-left px-3 py-2 text-sm rounded-md text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            🎭 Persona
          </button>
          <button
            onClick={() => scrollToSection("privacy")}
            className="w-full text-left px-3 py-2 text-sm rounded-md text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            🔒 Privacy
          </button>
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
          {/* API Keys Section */}
          <section id="api-keys" className="mb-8 scroll-mt-24">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
              🔑 API Keys (Bring Your Own Key)
            </h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Use your own API keys for chat. When set, chat uses your keys instead of platform credits.
            </p>

            <div className="space-y-3">
              {Object.entries(PROVIDERS).map(([provider, config]) => {
                const saved = savedKeys[provider];
                const isLoading = savingProvider === provider || deletingProvider === provider;

                return (
                  <div
                    key={provider}
                    className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                  >
                    {saved ? (
                      /* Saved key - horizontal display */
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 w-24 shrink-0">
                          <span className="text-lg">{config.icon}</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{config.name}</span>
                        </div>
                        <div className="flex-1 flex items-center gap-2 min-w-0">
                          <span className="font-mono text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                            {saved.key_hint}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            saved.is_valid ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          }`}>
                            {saved.is_valid ? "✓" : "✗"}
                          </span>
                          {saved.preferred_model && (
                            <span className="text-xs text-gray-400 truncate hidden sm:inline">
                              {saved.preferred_model}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteKey(provider)}
                          disabled={isLoading}
                          className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 shrink-0"
                        >
                          {deletingProvider === provider ? "..." : "Remove"}
                        </button>
                      </div>
                    ) : (
                      /* Input form - horizontal */
                      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                        <div className="flex items-center gap-2 w-24 shrink-0">
                          <span className="text-lg">{config.icon}</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{config.name}</span>
                        </div>
                        <PasswordInput
                          id={`api-key-${provider}`}
                          value={keyInputs[provider] || ""}
                          onChange={(v) =>
                            setKeyInputs((prev) => ({ ...prev, [provider]: v }))
                          }
                          placeholder={config.placeholder}
                          className="flex-1 min-w-[120px] rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        />
                        {availableModels[provider]?.length > 0 && (
                          <select
                            value={selectedModels[provider] || defaultModels[provider] || ""}
                            onChange={(e) =>
                              setSelectedModels((prev) => ({
                                ...prev,
                                [provider]: e.target.value,
                              }))
                            }
                            className="w-32 rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                          >
                            {availableModels[provider].map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.name.replace(" (default)", "")}
                              </option>
                            ))}
                          </select>
                        )}
                        <button
                          onClick={() => handleSaveKey(provider)}
                          disabled={isLoading || !keyInputs[provider]?.trim()}
                          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50 shrink-0"
                        >
                          {savingProvider === provider ? "..." : "Save"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {loadingKeys && (
              <div className="mt-4 text-center text-sm text-gray-500">
                Loading saved keys...
              </div>
            )}
            
            <p className="mt-3 text-xs text-gray-400">
              Model selection controls which model is used when chatting with your key.
            </p>
          </section>

          {/* Preferences Section */}
          <section id="preferences" className="mb-8 scroll-mt-24">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
              ⚙️ Preferences
            </h2>

        <div className="space-y-4">
          {/* Dark Mode Toggle */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Dark Mode
                </label>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Switch between light and dark themes
                </p>
              </div>
              <button
                onClick={() => handleDarkModeChange(!darkMode)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  darkMode ? "bg-blue-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    darkMode ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Language Selector */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Language
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search languages..."
              value={languageDropdownOpen ? languageSearch : (LANGUAGE_OPTIONS.find(o => o.value === language)?.label || "Auto-detect")}
              onChange={(e) => {
                setLanguageSearch(e.target.value);
                setLanguageDropdownOpen(true);
              }}
              onFocus={() => {
                setLanguageDropdownOpen(true);
                setLanguageSearch("");
              }}
              onBlur={() => {
                // Delay to allow click on dropdown
                setTimeout(() => setLanguageDropdownOpen(false), 200);
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {languageDropdownOpen && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                {LANGUAGE_OPTIONS
                  .filter(opt => 
                    opt.label.toLowerCase().includes(languageSearch.toLowerCase()) ||
                    opt.value.toLowerCase().includes(languageSearch.toLowerCase())
                  )
                  .slice(0, 20)
                  .map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        handleLanguageChange(opt.value);
                        setLanguageDropdownOpen(false);
                        setLanguageSearch("");
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                        language === opt.value ? "bg-blue-100 font-medium" : ""
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                {LANGUAGE_OPTIONS.filter(opt => 
                  opt.label.toLowerCase().includes(languageSearch.toLowerCase()) ||
                  opt.value.toLowerCase().includes(languageSearch.toLowerCase())
                ).length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">No languages found</div>
                )}
              </div>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Search from 99+ languages. Controls transcription language detection.
          </p>
          </div>
        </div>
      </section>

          {/* Subscriptions Section */}
          <section id="subscriptions" className="mb-8 scroll-mt-24">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
              📡 Subscriptions
            </h2>

            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Automatically transcribe new episodes from my subscriptions
                  </label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    When enabled, Echonour automatically transcribes new episodes from podcasts you've subscribed to. Default: off.
                  </p>
                </div>
                <button
                  onClick={() => handleAutoTranscribeSubsChange(!autoTranscribeSubs)}
                  disabled={savingAutoTranscribeSubs || loadingPrivacy}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                    autoTranscribeSubs ? "bg-blue-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      autoTranscribeSubs ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            </div>
          </section>

          {/* Persona Section */}
          <section id="speakers" className="mb-8 scroll-mt-24">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
              🎭 Persona
            </h2>
            
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                <strong>Persona mode</strong> lets you chat with an AI that adopts the reasoning style, vocabulary, and perspective of a specific speaker. 
                The AI analyzes hours of transcribed content to understand how each person thinks and communicates, then responds as they would. 
                Perfect for exploring "What would [speaker] say about this topic?"
              </p>
              <p className="mb-4 text-sm text-gray-500 dark:text-gray-500">
                Enable or disable speakers below. Only speakers with 9+ hours of transcribed content are available.
              </p>
              
              {loadingSpeakers ? (
                <p className="text-sm text-gray-500">Loading speakers...</p>
              ) : speakers.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No speakers available yet. Speakers become available once they have 9+ hours of transcribed content.
                </p>
              ) : (
                <div className="space-y-3">
                  {speakers.map((speaker) => (
                    <div key={speaker.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-purple-200 text-purple-700 text-sm font-bold flex items-center justify-center shrink-0">
                          {speaker.canonical_name?.charAt(0) || "?"}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                            {speaker.canonical_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {Math.round(speaker.total_speaking_ms / 3600000)} hours • {speaker.episode_count} episodes
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleSpeakerToggle(speaker.id, !speaker.enabled)}
                        disabled={togglingSpeaker === speaker.id}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 ${
                          speaker.enabled ? "bg-purple-600" : "bg-gray-200"
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            speaker.enabled ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section id="privacy" className="mb-8 scroll-mt-24">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
              🔒 Privacy
            </h2>

            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Training Data Opt-Out
                  </label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    By default, anonymized chat data may be used to improve Echonour.
                    Toggle this on to opt out completely.
                  </p>
                </div>
                <button
                  onClick={() => handleTrainingOptOutChange(!trainingOptOut)}
                  disabled={savingPrivacy || loadingPrivacy}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                    trainingOptOut ? "bg-blue-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      trainingOptOut ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
              {trainingOptOut && (
                <p className="mt-3 text-xs text-green-600">
                  ✓ You have opted out. Your chat data will not be used for training.
                </p>
              )}
            </div>

            {/* Voiceprint Consent */}
            <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Voiceprint for Private Uploads
                  </label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enable speaker recognition for your uploaded audio files.
                    Voiceprints help identify speakers across your private recordings.
                  </p>
                </div>
                <button
                  onClick={() => handleUploadConsentChange(!uploadConsent)}
                  disabled={savingConsent || loadingPrivacy}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                    uploadConsent ? "bg-blue-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      uploadConsent ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
              {uploadConsent && (
                <p className="mt-3 text-xs text-green-600">
                  ✓ Voiceprints will be extracted from your uploaded files.
                </p>
              )}
            </div>
          </section>
      </main>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 rounded-lg px-4 py-3 shadow-lg ${
            toast.type === "success"
              ? "bg-green-600 text-white"
              : "bg-red-600 text-white"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
